// SOLO mode: the CHART COLUMN in its own OS window, seeded to one symbol.
// This IS Detach. A detached window renders the parent's chart column
// identically — chart header with every pill, the chart with every overlay
// and card, the verdict ribbon, the trade band — and nothing else: no
// watchlist rail, no heat strip (those are the parent's workspace, not the
// chart). Each window owns its own WebSocket (lib/ws.ts is a per-window
// singleton) and its own Firebase session resolve. Feeds that only the rail
// renders are skipped in a detached window, and fleet-wide alarm SOUNDS play
// from the parent only, so five windows never chime five times.
//
// Enabled by `?symbol=SYM` in the URL (optional `&tf=1|3|5|15|30`). Parsed once
// at module load.

export type Solo = { symbol: string; tf: string } | null;

const TFS = new Set(['1', '3', '5', '15', '30']);

export const SOLO: Solo = (() => {
  try {
    if (typeof location === 'undefined') return null;
    const q = new URLSearchParams(location.search);
    const raw = (q.get('symbol') || '').trim().toUpperCase();
    // Ticker shape only — a garbled param renders the normal terminal instead
    // of pinning the tab to junk.
    if (!/^[A-Z][A-Z0-9.\-]{0,9}$/.test(raw)) return null;
    const tf = q.get('tf') || '1';
    return { symbol: raw, tf: TFS.has(tf) ? tf : '1' };
  } catch {
    return null;
  }
})();

/** URL that opens `sym` as a solo tab, preserving the mock/live mode params. */
export function soloUrl(sym: string, tf?: string): string {
  const q = new URLSearchParams();
  q.set('symbol', sym);
  if (tf && TFS.has(tf)) q.set('tf', tf);
  try {
    const cur = new URLSearchParams(location.search);
    if (cur.has('mock')) q.set('mock', cur.get('mock') || '');
    if (cur.has('live')) q.set('live', cur.get('live') || '');
  } catch { /* ignore */ }
  return (typeof location !== 'undefined' ? location.pathname : '/') + '?' + q.toString();
}

/** Detach: open `sym` as its OWN OS-level window — the full Terminal seeded
    to the symbol. `popup` drops the tab strip/omnibox, so the window is a
    bare terminal surface you can drag onto any monitor and tile four across.
    One window per symbol: the window name dedupes, so Detach on an already
    detached symbol focuses its window instead of stacking a duplicate. */
const openWins = new Map<string, Window>();
export function openSoloWindow(sym: string, tf?: string) {
  // Already open (and not closed by the operator) → just bring it forward.
  // Re-targeting a named window by URL would RELOAD it (dropping its WS,
  // cards and scroll) — focusing keeps the live window exactly as it is.
  const had = openWins.get(sym);
  if (had && !had.closed) { try { had.focus(); return; } catch { /* fall through */ } }
  const sw = (typeof screen !== 'undefined' && screen.availWidth) || 1600;
  const sh = (typeof screen !== 'undefined' && screen.availHeight) || 1000;
  const w = Math.min(1440, Math.round(sw * 0.8));
  const h = Math.min(900, Math.round(sh * 0.85));
  const left = Math.max(0, Math.round((sw - w) / 2) + 40);
  const top = Math.max(0, Math.round((sh - h) / 2));
  const feats = 'popup=yes,width=' + w + ',height=' + h + ',left=' + left + ',top=' + top + ',resizable=yes';
  const win = window.open(soloUrl(sym, tf), 'legend-solo-' + sym, feats);
  if (win) { openWins.set(sym, win); win.focus(); }
}
